import { CaptionSegment, parseCaptionSegments } from '../../remotion/shared/metadata/captions';
import { VolumePoint, sortAndValidatePoints } from '../../remotion/shared/metadata/envelope';
import { TimelineEntry } from '../../remotion/shared/metadata/timeline';
import { stringValue } from '../../remotion/shared/metadata/values';
import { NarrationTracks } from '../../remotion/shared/types';
import { clipSubtitleEnabled } from './globalToggles';

export type { CaptionSegment, VolumePoint };

/**
 * The narration lane's data: caption segments and the music/narrator track
 * timings, read out of clip metadata and written back to it.
 *
 * Two renderer behaviours the editor exists to stay out of, both enforced here
 * rather than in the components:
 *  - segments that overlap render last-writer-wins, which degrades the karaoke
 *    pacing, so every create/move/resize snaps against its neighbours;
 *  - segments past the composition end are clamped by the renderer with a
 *    warning, so nothing here may leave the clip's duration.
 */

/** Shorter than this a segment is unreadable, and a drag can no longer grab it. */
export const MIN_SEGMENT_SECONDS = 0.3;
/** How long a fresh segment is when there is room for it. */
export const NEW_SEGMENT_SECONDS = 2;
export const NEW_SEGMENT_TEXT = 'New caption';
/** A block may start no later than this before the clip ends. */
export const MIN_BLOCK_SECONDS = 0.5;

const round3 = (value: number): number => Number(value.toFixed(3));
const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));

// ==================== Caption segments ====================

export const readCaptionSegments = (clipMetadata: Record<string, any>): CaptionSegment[] =>
  parseCaptionSegments(clipMetadata?.caption_segments ?? clipMetadata?.captionSegments);

/**
 * Segments as the metadata value. An empty list serializes to `undefined`, which
 * the store turns into a deleted key — an empty `[]` left behind would still read
 * as "segments authored", and precedence keys off presence, not emptiness.
 */
export const serializeCaptionSegments = (segments: CaptionSegment[]): string | undefined =>
  segments.length === 0
    ? undefined
    : JSON.stringify(
        segments.map((segment) => ({
          id: segment.id,
          text: segment.text,
          start: round3(segment.start),
          end: round3(segment.end),
          ...(segment.posX === undefined ? {} : { pos_x: round3(segment.posX) }),
          ...(segment.posY === undefined ? {} : { pos_y: round3(segment.posY) }),
          ...(segment.scale === undefined ? {} : { scale: round3(segment.scale) }),
        }))
      );

/** An id no existing segment uses — duplicates make two blocks indistinguishable. */
export const nextSegmentId = (segments: CaptionSegment[]): string => {
  const taken = new Set(segments.map((segment) => segment.id));
  for (let index = segments.length + 1; ; index += 1) {
    const candidate = `seg-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
};

/** The gap around `atSeconds`, bounded by the neighbours and the clip's end. */
export const freeSlotAt = (
  segments: CaptionSegment[],
  atSeconds: number,
  clipSeconds: number
): { start: number; end: number } | null => {
  const at = clamp(atSeconds, 0, clipSeconds);
  const sorted = [...segments].sort((left, right) => left.start - right.start);
  if (sorted.some((segment) => at >= segment.start && at < segment.end)) return null;

  const previousEnd = sorted
    .filter((segment) => segment.end <= at)
    .reduce((latest, segment) => Math.max(latest, segment.end), 0);
  const nextStart = sorted
    .filter((segment) => segment.start >= at)
    .reduce((earliest, segment) => Math.min(earliest, segment.start), clipSeconds);

  const start = Math.max(at, previousEnd);
  const end = Math.min(start + NEW_SEGMENT_SECONDS, nextStart, clipSeconds);
  return end - start >= MIN_SEGMENT_SECONDS ? { start: round3(start), end: round3(end) } : null;
};

/** A new segment in the gap at `atSeconds`, or the list unchanged when it will not fit. */
export const createSegment = (
  segments: CaptionSegment[],
  atSeconds: number,
  clipSeconds: number,
  text: string = NEW_SEGMENT_TEXT
): { segments: CaptionSegment[]; created: CaptionSegment | null } => {
  const slot = freeSlotAt(segments, atSeconds, clipSeconds);
  if (!slot) return { segments, created: null };
  const created: CaptionSegment = { id: nextSegmentId(segments), text, ...slot };
  return { segments: sortSegments([...segments, created]), created };
};

export const sortSegments = (segments: CaptionSegment[]): CaptionSegment[] =>
  [...segments].sort((left, right) => left.start - right.start);

const neighbours = (
  segments: CaptionSegment[],
  id: string
): { lowerBound: number; upperBound: number; segment: CaptionSegment | undefined } => {
  const sorted = sortSegments(segments);
  const index = sorted.findIndex((segment) => segment.id === id);
  return {
    segment: index < 0 ? undefined : sorted[index],
    lowerBound: index > 0 ? sorted[index - 1].end : 0,
    upperBound: index >= 0 && index + 1 < sorted.length ? sorted[index + 1].start : Number.POSITIVE_INFINITY,
  };
};

/** Slides a segment along the timeline, keeping its length and clearing its neighbours. */
export const moveSegment = (
  segments: CaptionSegment[],
  id: string,
  startSeconds: number,
  clipSeconds: number
): CaptionSegment[] => {
  const { segment, lowerBound, upperBound } = neighbours(segments, id);
  if (!segment) return segments;
  const length = segment.end - segment.start;
  const ceiling = Math.min(upperBound, clipSeconds);
  const start = clamp(startSeconds, lowerBound, Math.max(lowerBound, ceiling - length));
  // With no room between the neighbour and the clip's end the segment has to be
  // shortened rather than pushed past it — and if that leaves nothing readable,
  // the move simply does not happen.
  const end = Math.min(start + length, ceiling);
  if (end - start < MIN_SEGMENT_SECONDS) return segments;
  return sortSegments(
    segments.map((candidate) =>
      candidate.id === id ? { ...candidate, start: round3(start), end: round3(end) } : candidate
    )
  );
};

/** Retimes one edge, snapping against the neighbour it is heading for. */
export const resizeSegment = (
  segments: CaptionSegment[],
  id: string,
  edge: 'start' | 'end',
  seconds: number,
  clipSeconds: number
): CaptionSegment[] => {
  const { segment, lowerBound, upperBound } = neighbours(segments, id);
  if (!segment) return segments;

  const next =
    edge === 'start'
      ? { start: clamp(seconds, lowerBound, segment.end - MIN_SEGMENT_SECONDS), end: segment.end }
      : {
          start: segment.start,
          end: clamp(seconds, segment.start + MIN_SEGMENT_SECONDS, Math.min(upperBound, clipSeconds)),
        };

  return sortSegments(
    segments.map((candidate) =>
      candidate.id === id
        ? { ...candidate, start: round3(next.start), end: round3(next.end) }
        : candidate
    )
  );
};

export const updateSegment = (
  segments: CaptionSegment[],
  id: string,
  patch: Partial<CaptionSegment>
): CaptionSegment[] =>
  segments.map((segment) => (segment.id === id ? { ...segment, ...patch } : segment));

export const deleteSegment = (segments: CaptionSegment[], id: string): CaptionSegment[] =>
  segments.filter((segment) => segment.id !== id);

// ==================== Converting the legacy subtitle sources ====================

/**
 * Legacy subtitles exist and nothing has been converted yet — the only state in
 * which the conversion is offered, since segments suppress both legacy sources
 * outright and running it twice would duplicate them.
 */
export const canConvertLegacySubtitles = (
  segments: CaptionSegment[],
  entries: TimelineEntry[],
  clipMetadata: Record<string, any>
): boolean =>
  segments.length === 0 &&
  (entries.some((entry) => entry.slide.text.trim() !== '') ||
    stringValue(clipMetadata?.subtitleText).trim() !== '');

/**
 * The conversion reproduces what is on screen NOW, so it follows the renderer's
 * own precedence (`subtitlePlan`): a clip-level `subtitleText` wins outright and
 * becomes one full-length segment, and only a clip without one converts its
 * per-slide texts, one segment per slide window. Preferring the slide texts
 * would silently change the captions of every clip that carries both.
 *
 * Windows are cut at the next slide's start so a transition overlap cannot put
 * two segments on screen at once.
 *
 * The legacy keys stay stored and simply go inert — precedence, not deletion, is
 * what turns them off, so the conversion is reversible by clearing the segments.
 */
export const convertLegacySubtitles = (args: {
  entries: TimelineEntry[];
  clipMetadata: Record<string, any>;
  totalSeconds: number;
  fps: number;
}): CaptionSegment[] => {
  const { entries, clipMetadata, totalSeconds, fps } = args;

  const clipText = stringValue(clipMetadata?.subtitleText);
  if (clipText.trim() !== '') {
    return totalSeconds < MIN_SEGMENT_SECONDS
      ? []
      : [{ id: 'seg-1', text: clipText, start: 0, end: round3(totalSeconds) }];
  }

  const segments: CaptionSegment[] = [];

  entries.forEach((entry, index) => {
    const text = entry.slide.text.trim();
    if (text === '') return;
    const start = entry.fromFrame / fps;
    const ownEnd = (entry.fromFrame + entry.durationInFrames) / fps;
    const nextStart = entries[index + 1] ? entries[index + 1].fromFrame / fps : totalSeconds;
    const end = Math.min(ownEnd, nextStart, totalSeconds);
    if (end - start < MIN_SEGMENT_SECONDS) return;
    segments.push({
      id: `seg-${segments.length + 1}`,
      text: entry.slide.text,
      start: round3(start),
      end: round3(end),
    });
  });

  return segments;
};

// ==================== Stored text ↔ edited text ====================

/**
 * `|` is this app's storage encoding for a line break in caption text (the
 * storyboard writes it, `SlideInspector` maps it, and a converted slide text
 * arrives already carrying pipes). It is NOT a renderer token — `SubtitleOverlay`
 * splits on whitespace — so nobody should ever be asked to type one.
 */
export const fromStoredText = (text: string): string => text.replace(/\|/g, '\n');
export const toStoredText = (text: string): string => text.replace(/\n/g, '|');

// ==================== Music / narrator track timing ====================

export type TrackLane = 'music' | 'narrator';

export interface TrackKeys {
  offset: string;
  trimStart: string;
  trimEnd: string;
  volumePoints: string;
}

export const TRACK_KEYS: Record<TrackLane, TrackKeys> = {
  music: {
    offset: 'music_offset',
    trimStart: 'music_trim_start',
    trimEnd: 'music_trim_end',
    volumePoints: 'music_volume_points',
  },
  narrator: {
    offset: 'narrator_offset',
    trimStart: 'narrator_trim_start',
    trimEnd: 'narrator_trim_end',
    volumePoints: 'narrator_volume_points',
  },
};

export interface TrackTiming {
  /** Seconds of silence before the track starts on the clip timeline. */
  offsetSeconds: number;
  /** Seconds into the SOURCE file — unrelated to the offset above. */
  trimStartSeconds?: number;
  trimEndSeconds?: number;
  volumePoints: VolumePoint[];
}

const optionalSeconds = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
};

export const readTrackTiming = (clipMetadata: Record<string, any>, lane: TrackLane): TrackTiming => {
  const keys = TRACK_KEYS[lane];
  return {
    offsetSeconds: optionalSeconds(clipMetadata?.[keys.offset]) ?? 0,
    trimStartSeconds: optionalSeconds(clipMetadata?.[keys.trimStart]),
    trimEndSeconds: optionalSeconds(clipMetadata?.[keys.trimEnd]),
    volumePoints: sortAndValidatePoints(clipMetadata?.[keys.volumePoints]),
  };
};

/** Where a track's block sits on the clip timeline, given its probed source length. */
export const trackBlockSpan = (
  timing: TrackTiming,
  sourceSeconds: number | undefined
): { startSeconds: number; lengthSeconds: number | undefined } => {
  const start = timing.trimStartSeconds ?? 0;
  const end = timing.trimEndSeconds ?? sourceSeconds;
  const length = end === undefined ? undefined : Math.max(MIN_BLOCK_SECONDS, end - start);
  return { startSeconds: timing.offsetSeconds, lengthSeconds: length };
};

/**
 * A track drag, resolved against the source length and the clip's duration.
 *
 * `body` slides the block: only the offset moves. `start` trims the head while
 * pinning the block's right edge on the timeline (the NLE convention), so the
 * offset moves with the trim. `end` moves the tail. Nothing may start past the
 * clip's end, and no trim may leave the source.
 */
export const dragTrackTiming = (args: {
  timing: TrackTiming;
  mode: 'body' | 'start' | 'end';
  deltaSeconds: number;
  sourceSeconds?: number;
  clipSeconds: number;
}): { offsetSeconds: number; trimStartSeconds?: number; trimEndSeconds?: number } => {
  const { timing, mode, deltaSeconds, sourceSeconds, clipSeconds } = args;
  const latestStart = Math.max(0, clipSeconds - MIN_BLOCK_SECONDS);
  const trimStart = timing.trimStartSeconds ?? 0;
  const trimEnd = timing.trimEndSeconds ?? sourceSeconds;

  if (mode === 'body') {
    return {
      offsetSeconds: round3(clamp(timing.offsetSeconds + deltaSeconds, 0, latestStart)),
      trimStartSeconds: timing.trimStartSeconds,
      trimEndSeconds: timing.trimEndSeconds,
    };
  }

  if (mode === 'start') {
    const upper = (trimEnd ?? Number.POSITIVE_INFINITY) - MIN_BLOCK_SECONDS;
    // The offset cannot go below zero, which in turn caps how far the head trim
    // can travel: the pair moves together or the right edge would drift.
    const lower = Math.max(0, trimStart - timing.offsetSeconds);
    const nextStart = clamp(trimStart + deltaSeconds, lower, Math.max(lower, upper));
    const applied = nextStart - trimStart;
    return {
      offsetSeconds: round3(clamp(timing.offsetSeconds + applied, 0, latestStart)),
      trimStartSeconds: round3(nextStart),
      trimEndSeconds: timing.trimEndSeconds,
    };
  }

  const ceiling = sourceSeconds ?? Number.POSITIVE_INFINITY;
  const base = trimEnd ?? sourceSeconds;
  if (base === undefined) {
    // Without a probed length there is no tail to move — the block already spans
    // "unknown", and inventing an end would trim audio nobody asked to trim.
    return { offsetSeconds: timing.offsetSeconds, trimStartSeconds: timing.trimStartSeconds };
  }
  return {
    offsetSeconds: timing.offsetSeconds,
    trimStartSeconds: timing.trimStartSeconds,
    trimEndSeconds: round3(clamp(base + deltaSeconds, trimStart + MIN_BLOCK_SECONDS, ceiling)),
  };
};

/** Envelope points as the metadata value; an empty envelope deletes the key. */
export const serializeVolumePoints = (points: VolumePoint[]): string | undefined =>
  points.length === 0
    ? undefined
    : JSON.stringify(points.map((point) => ({ t: round3(point.t), v: round3(point.v) })));

// ==================== Preview props ====================

/**
 * The narration half of the `<Player>`'s input props, read exactly as the
 * renderer's `narrationTracks` reads it (normalize-reel-metadata.ts) — including
 * the clamp to the composition's length, which the renderer only applies with a
 * warning. Getting there first is the point: what plays here is what renders.
 */
export const narrationInputProps = (
  clipMetadata: Record<string, any>,
  totalSeconds: number
): NarrationTracks => {
  const hidden = !clipSubtitleEnabled(clipMetadata);
  const captionSegments = hidden ? [] : clampSegmentsToClip(readCaptionSegments(clipMetadata), totalSeconds);
  const narrator = readTrackTiming(clipMetadata, 'narrator');
  const music = readTrackTiming(clipMetadata, 'music');

  return {
    ...(hidden ? { subtitlesHidden: true } : {}),
    ...(captionSegments.length === 0 ? {} : { captionSegments }),
    ...(narrator.offsetSeconds > 0 ? { narratorOffsetSeconds: narrator.offsetSeconds } : {}),
    ...(narrator.trimStartSeconds === undefined ? {} : { narratorTrimStartSeconds: narrator.trimStartSeconds }),
    ...(narrator.trimEndSeconds === undefined ? {} : { narratorTrimEndSeconds: narrator.trimEndSeconds }),
    ...(narrator.volumePoints.length === 0 ? {} : { narratorVolumePoints: narrator.volumePoints }),
    ...(music.offsetSeconds > 0 ? { musicOffsetSeconds: music.offsetSeconds } : {}),
    ...(music.trimStartSeconds === undefined ? {} : { musicTrimStartSeconds: music.trimStartSeconds }),
    ...(music.trimEndSeconds === undefined ? {} : { musicTrimEndSeconds: music.trimEndSeconds }),
    ...(music.volumePoints.length === 0 ? {} : { musicVolumePoints: music.volumePoints }),
  };
};

/** Drops what starts past the end and clamps what overruns it. */
export const clampSegmentsToClip = (
  segments: CaptionSegment[],
  totalSeconds: number
): CaptionSegment[] => {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return segments;
  return segments
    .filter((segment) => segment.start < totalSeconds)
    .map((segment) => (segment.end > totalSeconds ? { ...segment, end: totalSeconds } : segment));
};

// ==================== Per-slide native audio ====================

export const NATIVE_AUDIO_KEYS = {
  volumePoints: 'native_audio_volume_points',
  trimStart: 'native_audio_trim_start',
  trimEnd: 'native_audio_trim_end',
} as const;

export const readNativeVolumePoints = (metadata: Record<string, any>): VolumePoint[] =>
  sortAndValidatePoints(
    metadata?.[NATIVE_AUDIO_KEYS.volumePoints] ?? metadata?.nativeAudioVolumePoints
  );
